<template>
  <validation-observer ref="form">
    <v-form @submit.prevent="onSaveAndAddNew">
      <v-container fluid>
        <v-row
          justify="center"
          :no-gutters="$vuetify.breakpoint.smAndDown"
        >
          <v-col cols="12" xl="9" class="text-justify">
            <v-card flat class="transparent">
              <v-card-text class="pt-0">
                <template v-if="!isLinePartMoldCustomerMode">
                  <v-row>
                    <v-col cols="12" sm="4">
                      <v-autocomplete
                        clearable
                        filled
                        :label="$t('assemblyPlanning.model')"
                        :items="modelList"
                        item-text="model"
                        item-value="model"
                        v-model="selectedModel"
                        @change="fetchPartByModel"
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-autocomplete
                        clearable
                        filled
                        :label="$t('assemblyPlanning.variant')"
                        :items="variantList"
                        :disabled="saving || !selectedModel"
                        :loading="fetchingMatrix"
                        item-text="variant"
                        item-value="variant"
                        v-model="selectedVariant"
                        @change="filterCustomer"
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-combobox
                        clearable
                        filled
                        :label="$t('assemblyPlanning.customer')"
                        :items="customers"
                        item-text="customername"
                        item-value="customername"
                        :disabled="saving || !selectedVariant"
                        v-model="selectedCustomer"
                        @change="filterLine"
                      ></v-combobox>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" sm="4">
                      <v-autocomplete
                        clearable
                        filled
                        :label="$t('assemblyPlanning.line')"
                        :items="lineList"
                        item-text="linename"
                        item-value="linename"
                        :disabled="saving || !selectedCustomer"
                        v-model="selectedLine"
                        @change="filterPart"
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="8">
                      <v-text-field
                        :label="$t('assemblyPlanning.remark')"
                        outlined
                        v-model="plan.remark"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </template>
                <template v-else>
                  <v-row>
                    <v-col cols="12" sm="4">
                      <v-autocomplete
                        clearable
                        filled
                        :label="$t('assemblyPlanning.line')"
                        :items="lineList"
                        item-text="linename"
                        item-value="linename"
                        :disabled="saving"
                        :loading="fetchingMatrix"
                        v-model="selectedLine"
                        @change="onLineSelection"
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-autocomplete
                        clearable
                        filled
                        :label="$t('assemblyPlanning.part')"
                        :items="partList"
                        item-text="partname"
                        item-value="partname"
                        return-object
                        :disabled="saving || !selectedLine"
                        v-model="selectedPart"
                        @change="onPartSelection"
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-autocomplete
                        clearable
                        filled
                        :label="$t('assemblyPlanning.equipment')"
                        :items="equipmentList"
                        item-text="equipmentname"
                        item-value="equipmentname"
                        :disabled="saving || !selectedPart"
                        v-model="selectedEquipment"
                        @change="onEquipmentSelection"
                      ></v-autocomplete>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" sm="4" v-if="isShowColor">
                      <validation-provider
                        name="color"
                        :rules="isShowColor ? 'required' : ''"
                        #default="{ errors }"
                      >
                        <v-autocomplete
                          clearable
                          filled
                          :label="$t('assemblyPlanning.color')"
                          :items="filteredColorList"
                          :disabled="saving || !selectedPart"
                          :error-messages="errors"
                          item-text="colorname"
                          item-value="colorname"
                          v-model="selectedColor"
                          :title="selectedColor || ''"
                        >
                          <template #item="{ item }">
                            <v-list-item-content>
                              <v-list-item-title v-text="item.colorname"></v-list-item-title>
                            </v-list-item-content>
                          </template>
                        </v-autocomplete>
                      </validation-provider>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <validation-provider
                        name="customer"
                        rules="required"
                        #default="{ errors }"
                      >
                        <v-combobox
                          clearable
                          filled
                          :label="$t('assemblyPlanning.customer')"
                          :items="customers"
                          item-text="customer"
                          item-value="customer"
                          :disabled="saving || !selectedEquipment"
                          :error-messages="errors"
                          v-model="selectedCustomer"
                          @change="onCustomerSelection"
                        ></v-combobox>
                      </validation-provider>
                    </v-col>
                    <v-col cols="12" :sm="isShowColor ? 4 : 8">
                      <v-text-field
                        :label="$t('assemblyPlanning.remark')"
                        outlined
                        v-model="plan.remark"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </template>
                <div class="title mb-2">
                  {{ $t('assemblyPlanning.operationalParams') }}
                  <v-btn
                    small
                    outlined
                    class="text-none"
                    :disabled="!selectedMatrix || saving"
                    @click="editParams = !editParams"
                    color="primary"
                  >
                    <template v-if="!editParams">
                      <v-icon left small>mdi-pencil-outline</v-icon>
                      {{ $t('assemblyPlanning.editButton') }}
                    </template>
                    <template v-else>
                      <v-icon left small>mdi-check</v-icon>
                      {{ $t('assemblyPlanning.doneButton') }}
                    </template>
                  </v-btn>
                </div>
                <v-row v-for="macdetails in plan.machinedetails" :key="macdetails.machinename">
                  <v-col cols="12" sm="3">
                    <validation-provider
                      name="machinename"
                      rules="required"
                      #default="{ errors }"
                    >
                      <v-text-field
                        :label="$t('assemblyPlanning.machine')"
                        type="name"
                        disabled
                        :error-messages="errors"
                        prepend-inner-icon="mdi-clock-fast"
                        outlined
                        v-model="macdetails.machinename"
                        hide-details="auto"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <validation-provider
                      name="cycletime"
                      :rules="cycleTimeRules"
                      #default="{ errors }"
                    >
                      <v-text-field
                        :label="$t('assemblyPlanning.cycletime')"
                        type="number"
                        step="any"
                        :disabled="!editParams || saving"
                        :error-messages="errors"
                        prepend-inner-icon="mdi-clock-fast"
                        outlined
                        :suffix="$t('assemblyPlanning.secs')"
                        v-model.number="macdetails.stdcycletime"
                        hide-details="auto"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <validation-provider
                      name="delaytime"
                      rules="required|min_value:0"
                      #default="{ errors }"
                    >
                      <v-text-field
                        :label="$t('assemblyPlanning.delayTime')"
                        type="number"
                        :disabled="!editParams || saving"
                        :error-messages="errors"
                        prepend-inner-icon="mdi-cached"
                        outlined
                        :suffix="$t('assemblyPlanning.secs')"
                        v-model="macdetails.delaytime"
                        hide-details="auto"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" sm="3">
                      <validation-provider
                        name="activecavity"
                        rules="required|min_value:1"
                        #default="{ errors }"
                      >
                        <v-text-field
                          :label="$t('assemblyPlanning.activeCavity')"
                          type="number"
                          :disabled="!editParams || saving"
                          :error-messages="errors"
                          prepend-inner-icon="mdi-clock-fast"
                          outlined
                          v-model="macdetails.activecavity"
                          hide-details="auto"
                        ></v-text-field>
                      </validation-provider>
                    </v-col>
                </v-row>
                <div class="title mt-4 mb-2">
                  {{ $t('assemblyPlanning.planDetails') }}
                </div>
                <v-row>
                  <v-col cols="12" sm="4">
                    <validation-provider
                      name="quantity"
                      :rules="`required|numeric|min_value:1|multiple_of:${plan.activecavity}`"
                      #default="{ errors }"
                    >
                      <v-text-field
                        :label="$t('assemblyPlanning.quantity')"
                        type="number"
                        :disabled="saving"
                        :error-messages="errors"
                        prepend-inner-icon="mdi-package-variant-closed"
                        outlined
                        :suffix="$t('assemblyPlanning.pieces')"
                        v-model="plan.plannedquantity"
                        hide-details="auto"
                        @change="onQuantityChange"
                        :hint="`Should be multiple of active cavity(${plan.activecavity})`"
                      ></v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <validation-provider
                      name="scheduledstart"
                      rules="required|greater_than_now"
                      #default="{ errors }"
                    >
                      <v-text-field
                        :label="$t('assemblyPlanning.scheduledStartTime')"
                        type="datetime-local"
                        :error-messages="errors"
                        prepend-inner-icon="mdi-calendar-check-outline"
                        outlined
                        v-model="plan.scheduledstart"
                        hide-details="auto"
                        :disabled="saving"
                        @change="fetchEstimatedEnd"
                      >
                        <template #append-outer>
                          <v-tooltip bottom>
                            <template #activator="{ on, attrs }">
                              <v-icon
                                v-text="'$info'"
                                v-on="on"
                                v-bind="attrs"
                              ></v-icon>
                            </template>
                            <ul>
                              <li>
                                Scheduled start time is only used in calculating
                                planned vs actual performance.
                              </li>
                              <li>
                                Updating scheduled start time will not affect
                                the execution order of the plan.
                              </li>
                              <li>
                                Plans are picked automatically in FIFO fashion.
                              </li>
                              <li>
                                To update the execution order, please visit
                                <strong>re-order plans section</strong>.
                              </li>
                            </ul>
                          </v-tooltip>
                        </template>
                      </v-text-field>
                    </validation-provider>
                  </v-col>
                  <v-col cols="6" sm="2">
                    <v-checkbox
                      hide-details
                      :label="$t('assemblyPlanning.markAsStar')"
                      :disabled="saving"
                      v-model="plan.starred"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="6" sm="2">
                    <v-checkbox
                      hide-details
                      :label="$t('assemblyPlanning.markAsTrial')"
                      :disabled="saving"
                      v-model="plan.trial"
                    ></v-checkbox>
                  </v-col>
                </v-row>
                <div>
                  {{ $t('assemblyPlanning.estimatedEnd') }}
                  <strong>{{ estimatedEndDisplay }}</strong>
                </div>
                <v-alert
                  v-if="validationErrors.length"
                  type="error"
                  dense
                  text
                  class="mt-4 mb-0"
                >
                  <div class="font-weight-medium mb-2">
                    Please fix the following validation issues:
                  </div>
                  <ul class="mb-0">
                    <li v-for="error in validationErrors" :key="error">
                      {{ error }}
                    </li>
                  </ul>
                </v-alert>
              </v-card-text>
              <v-card-actions>
                <v-btn
                  text
                  color="primary"
                  class="text-none"
                  :disabled="saving"
                  @click="exit"
                >
                  {{ $t('assemblyPlanning.exit') }}
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn
                  outlined
                  color="primary"
                  class="text-none"
                  :disabled="savingAndNew
                    || !hasRequiredSelection
                    || !plan.plannedquantity"
                  :loading="savingAndExit"
                  @click="onSaveAndExit"
                >
                  {{ $t('assemblyPlanning.saveExit') }}
                </v-btn>
                <v-btn
                  color="primary"
                  class="text-none"
                  :disabled="savingAndExit || !hasRequiredSelection || !plan.plannedquantity"
                  :loading="savingAndNew"
                  type="submit"
                >
                  {{ $t('assemblyPlanning.saveAddNew') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
  </validation-observer>
</template>

<script>
import {
  mapState, mapActions, mapGetters, mapMutations,
} from 'vuex';
// import { sortArray } from '@shopworx/services/util/sort.service';
import { formatDate } from '@shopworx/services/util/date.service';
import planningConfigMixin, {
  buildPlanMachineDetails,
  fetchPartMatrixRecords,
  resolvePartNetweight,
} from '../../mixins/planning-config';

export default {
  name: 'AddPlan',
  mixins: [planningConfigMixin],
  data() {
    return {
      headers: [
        { text: this.$t('assemblyPlanning.part'), value: 'partname' },
        { text: this.$t('assemblyPlanning.activeCavity'), value: 'activecavity' },
        { text: this.$t('assemblyPlanning.quantity'), value: 'plannedquantity' },
      ],
      editParams: false,
      savingAndExit: false,
      savingAndNew: false,
      fetchingMatrix: false,
      validationErrors: [],
      variantList: [],
      // customerList: [],
      lineList: [],
      partList: [],
      equipmentList: [],
      customerOptions: [],
      colorList: [],
      partMatrixList: [],
      partMatrixListData: [],
      selectedModel: null,
      selectedVariant: null,
      selectedCustomer: null,
      selectedLine: null,
      selectedEquipment: null,
      selectedPart: null,
      selectedColor: null,
      plan: {},
      // remark: '',
    };
  },
  computed: {
    ...mapState('assemblyPlanning', ['machines', 'parts', 'partMatrix', 'customerList']),
    ...mapGetters('assemblyPlanning', ['selectedAsset', 'partMatrixTags']),
    ...mapGetters('helper', ['locale']),
    saving() {
      return this.savingAndExit || this.savingAndNew;
    },
    hasRequiredSelection() {
      if (this.isLinePartMoldCustomerMode) {
        return !!(
          this.selectedLine
          && this.selectedPart
          && this.selectedEquipment
          && (!this.isShowColor || this.selectedColor)
          && this.selectedCustomer
          && this.selectedMatrix
          && this.selectedMatrix.length
        );
      }
      return !!this.selectedPart;
    },
    cycleTimeRules() {
      return 'required|min_value:0.000001';
    },
    modelList() {
      return this.parts;
    },
    customers() {
      if (this.isLinePartMoldCustomerMode) {
        return this.customerOptions;
      }
      return this.customerList;
    },
    selectedMachineAssetId() {
      if (!this.isLinePartMoldCustomerMode || !this.selectedLine) {
        return null;
      }
      const lineMachines = (this.machines || [])
        .filter((machine) => machine.linename === this.selectedLine);
      const assetIds = [...new Set(
        lineMachines
          .map((machine) => machine.assetid ?? machine.assetId ?? null)
          .filter((assetId) => assetId !== null && assetId !== undefined && assetId !== ''),
      )];
      if (assetIds.length === 1) {
        return assetIds[0];
      }
      return null;
    },
    estimatedEndDisplay() {
      let res = '';
      if (this.plan.scheduledend) {
        res = formatDate(
          new Date(this.plan.scheduledend),
          'PPpp',
          { locale: this.locale },
        );
      }
      return res;
    },
    selectedMatrix() {
      if (this.isLinePartMoldCustomerMode) {
        if (
          this.selectedPart
          && this.selectedLine
          && this.selectedEquipment
        ) {
          const partMatrix = this.partMatrixListData.filter((matrix) => (
            matrix.partname === this.selectedPart.partname
            && matrix.linename === this.selectedLine
            && matrix.equipmentname === this.selectedEquipment
          ));
          const assetFilteredMatrix = this.selectedMachineAssetId
            ? partMatrix.filter((matrix) => (
              String(matrix.assetid ?? matrix.assetId ?? '') === String(this.selectedMachineAssetId)
            ))
            : [];
          const customerMatrix = this.selectedCustomer
            ? partMatrix.filter((matrix) => matrix.customer === this.selectedCustomer)
            : partMatrix;
          let selectedMatrix = partMatrix;
          if (assetFilteredMatrix.length) {
            selectedMatrix = assetFilteredMatrix;
          } else if (customerMatrix.length) {
            selectedMatrix = customerMatrix;
          }
          return selectedMatrix.length
            ? selectedMatrix.sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
            : null;
        }
        return null;
      }
      if (this.selectedPart && this.selectedLine) {
        const partMatrix = this.partMatrixList.filter((matrix) => (
          matrix.partname === this.selectedPart.partname
          && matrix.linename === this.selectedLine
        ));
        return partMatrix.length
          ? partMatrix.sort((a, b) => a.sequence - b.sequence)
          : null;
      }
      return null;
    },
    filteredColorList() {
      if (!this.isShowColor) {
        return [];
      }

      const normalizedColors = (this.colorList || [])
        .map((color) => ({
          ...color,
          colorname: color.colorname || color.color || color.name,
        }))
        .filter((color) => color.colorname);

      if (!this.selectedPart || !this.selectedPart.partname) {
        return normalizedColors;
      }

      const partName = this.selectedPart.partname;
      const partColors = normalizedColors.filter((color) => (
        !color.partname || color.partname === partName
      ));

      return partColors.length ? partColors : normalizedColors;
    },
    shots() {
      if (this.plan.plannedquantity && this.plan.activecavity) {
        return +this.plan.plannedquantity / +this.plan.activecavity;
      }
      return 0;
    },
  },
  async created() {
    await this.fetchCustomerList();
    this.initPlan();
    if (this.isLinePartMoldCustomerMode) {
      this.fetchingMatrix = true;
      this.partMatrixListData = await fetchPartMatrixRecords(this.$store);
      if (this.isShowColor) {
        this.colorList = await this.fetchColors();
      }
      this.setAvailableLines();
      this.fetchingMatrix = false;
    }
  },
  methods: {
    ...mapMutations('helper', ['setAlert']),
    ...mapActions('assemblyPlanning', [
      'fetchPartMatrix',
      'getScheduledEnd',
      'fetchLastPlan',
      'fetchColors',
      'createPlans',
      'fetchModel',
      'fetchCustomerList',
    ]),
    initPlan() {
      this.plan = {
        assetid: 0,
        partname: '',
        netweight: '',
        linename: '',
        activecavity: 1,
        machinedetails: [],
        plannedquantity: 0,
        scheduledstart: formatDate(new Date(Math.ceil(new Date() / 9e5) * 9e5), 'yyyy-MM-dd\'T\'HH:mm'),
        scheduledend: '',
        starred: false,
        trial: false,
        sortindex: 0,
        status: 'notStarted',
        moldname: '',
        toolname: '',
        remark: '',
      };
      this.validationErrors = [];
    },
    getUniqueRecords(records, key) {
      return [...new Map(
        records
          .filter((record) => record && record[key] !== undefined && record[key] !== null && record[key] !== '')
          .map((record) => [record[key], record]),
      ).values()];
    },
    setAvailableLines() {
      this.lineList = this.getUniqueRecords(this.partMatrixListData, 'linename');
    },
    clear() {
      this.initPlan();
      this.editParams = false;
      this.variantList = [];
      // this.customers = [];
      this.lineList = [];
      this.partList = [];
      this.equipmentList = [];
      this.customerOptions = [];
      this.selectedModel = null;
      this.selectedVariant = null;
      this.selectedCustomer = null;
      this.selectedLine = null;
      this.selectedEquipment = null;
      this.selectedPart = null;
      this.selectedColor = null;
      if (this.isLinePartMoldCustomerMode) {
        this.setAvailableLines();
      }
      this.$nextTick(() => {
        this.$refs.form.reset();
        this.validationErrors = [];
      });
    },
    validationFieldLabel(field) {
      const labels = {
        color: this.$t('assemblyPlanning.color'),
        customer: this.$t('assemblyPlanning.customer'),
        quantity: this.$t('assemblyPlanning.quantity'),
        scheduledstart: this.$t('assemblyPlanning.scheduledStartTime'),
        machinename: this.$t('assemblyPlanning.machine'),
        cycletime: this.$t('assemblyPlanning.cycletime'),
        delaytime: this.$t('assemblyPlanning.delayTime'),
        activecavity: this.$t('assemblyPlanning.activeCavity'),
      };
      return labels[field] || field;
    },
    collectValidationErrors() {
      const observer = this.$refs.form;
      if (!observer || !observer.errors) {
        return [];
      }
      return Object.entries(observer.errors).flatMap(([field, messages]) => {
        const list = Array.isArray(messages) ? messages : [messages];
        return list
          .filter((message) => message)
          .map((message) => `${this.validationFieldLabel(field)}: ${message}`);
      });
    },
    async fetchPartByModel() {
      if (this.isLinePartMoldCustomerMode) {
        return;
      }
      this.initPlan();
      this.variantList = [];
      // this.customers = [];
      this.lineList = [];
      this.partList = [];
      this.selectedVariant = null;
      this.selectedCustomer = null;
      this.selectedLine = null;
      this.selectedPart = null;
      this.selectedColor = null;
      if (this.selectedModel) {
        this.fetchingMatrix = true;
        this.variantList = await this.fetchModel({ model: this.selectedModel });
        this.fetchingMatrix = false;
      }
    },
    filterCustomer() {
      if (this.isLinePartMoldCustomerMode) {
        return;
      }
      this.initPlan();
      // this.customers = [];
      this.lineList = [];
      this.partList = [];
      this.selectedCustomer = null;
      this.selectedLine = null;
      this.selectedPart = null;
      this.selectedColor = null;
      // if (this.selectedVariant) {
      //   this.customerList = this.variantList
      //     .filter((variant) => variant.variant === this.selectedVariant);
      // }
    },
    filterLine() {
      if (this.isLinePartMoldCustomerMode) {
        return;
      }
      this.initPlan();
      this.lineList = [];
      this.partList = [];
      this.selectedLine = null;
      this.selectedPart = null;
      this.selectedColor = null;
      if (this.selectedCustomer) {
        // this.lineList = this.variantList;
        this.lineList = this.variantList
          .filter((variant) => variant.variant === this.selectedVariant);
      }
    },
    filterPart() {
      if (this.isLinePartMoldCustomerMode) {
        return;
      }
      this.initPlan();
      this.partList = [];
      this.selectedPart = null;
      this.selectedColor = null;
      if (this.selectedLine) {
        this.partList = this.lineList
          .filter((line) => line.linename === this.selectedLine);
        this.partMatrixList = this.partList;
        [this.selectedPart] = this.partList;
        this.setPlanParameters();
      }
    },
    onLineSelection() {
      this.initPlan();
      this.partList = [];
      this.equipmentList = [];
      this.customerOptions = [];
      this.selectedPart = null;
      this.selectedEquipment = null;
      this.selectedCustomer = null;
      this.selectedColor = null;
      if (this.selectedLine) {
        this.partList = this.getUniqueRecords(
          this.partMatrixListData.filter((matrix) => matrix.linename === this.selectedLine),
          'partname',
        );
      }
    },
    onPartSelection() {
      this.initPlan();
      this.equipmentList = [];
      this.customerOptions = [];
      this.selectedEquipment = null;
      this.selectedCustomer = null;
      this.selectedColor = null;
      if (this.selectedLine && this.selectedPart) {
        this.equipmentList = this.getUniqueRecords(
          this.partMatrixListData.filter((matrix) => (
            matrix.linename === this.selectedLine
            && matrix.partname === this.selectedPart.partname
          )),
          'equipmentname',
        );
      }
    },
    onEquipmentSelection() {
      this.initPlan();
      this.customerOptions = [];
      this.selectedCustomer = null;
      if (this.selectedLine && this.selectedPart && this.selectedEquipment) {
        this.customerOptions = this.getUniqueRecords(
          this.partMatrixListData.filter((matrix) => (
            matrix.linename === this.selectedLine
            && matrix.partname === this.selectedPart.partname
            && matrix.equipmentname === this.selectedEquipment
          )),
          'customer',
        );
        if (this.customerOptions.length === 1) {
          [this.selectedCustomer] = this.customerOptions.map((customer) => customer.customer);
          this.onCustomerSelection();
        }
      }
    },
    onCustomerSelection() {
      if (this.selectedCustomer) {
        this.setPlanParameters();
      }
    },
    async setPlanParameters() {
      const preservedPlan = {
        plannedquantity: this.plan.plannedquantity,
        scheduledstart: this.plan.scheduledstart,
        scheduledend: this.plan.scheduledend,
        starred: this.plan.starred,
        trial: this.plan.trial,
        sortindex: this.plan.sortindex,
        status: this.plan.status,
        remark: this.plan.remark,
      };
      this.initPlan();
      Object.assign(this.plan, preservedPlan);
      if (this.isLinePartMoldCustomerMode) {
        if (
          this.selectedPart
          && this.selectedLine
          && this.selectedEquipment
          && this.selectedCustomer
        ) {
          const [firstMatrix] = this.selectedMatrix || [];
          if (!firstMatrix) {
            return;
          }
          this.plan.assetid = this.selectedMachineAssetId
            || firstMatrix.assetid
            || firstMatrix.assetId
            || this.selectedPart.assetid;
          this.plan.partname = firstMatrix.partname;
          this.plan.netweight = resolvePartNetweight({
            parts: this.parts,
            partname: firstMatrix.partname,
            selectedPart: this.selectedPart,
            fallback: firstMatrix.netweight || '',
          });
          this.plan.model = firstMatrix.model;
          this.plan.variant = firstMatrix.variant;
          this.plan.customer = this.selectedCustomer
            || firstMatrix.customer
            || this.plan.customer
            || '';
          this.plan.linename = firstMatrix.linename || this.selectedLine;
          this.plan.moldname = firstMatrix.moldname || '';
          this.plan.toolname = firstMatrix.toolname || '';
          this.plan.activecavity = 1;
          if (this.isShowColor && !this.selectedColor && this.filteredColorList.length === 1) {
            [this.selectedColor] = this.filteredColorList.map((color) => color.colorname);
          }
          this.plan.machinedetails = buildPlanMachineDetails({
            selectedMatrix: this.selectedMatrix,
            machines: this.machines,
            lineName: this.plan.linename,
            assetId: this.selectedMachineAssetId,
          });
        }
        return;
      }
      if (this.selectedPart && this.selectedLine) {
        this.plan.assetid = this.selectedPart.assetid;
        this.plan.partname = this.selectedPart.partname;
        this.plan.netweight = resolvePartNetweight({
          parts: this.parts,
          partname: this.selectedPart.partname,
          selectedPart: this.selectedPart,
          fallback: '',
        });
        this.plan.model = this.selectedModel;
        this.plan.variant = this.selectedVariant;
        this.plan.customer = this.selectedCustomer
          || this.plan.customer
          || '';
        this.plan.linename = this.selectedLine;
        this.plan.activecavity = 1;
        this.plan.machinedetails = [];
        this.selectedMatrix.forEach((part) => {
          const {
            machinename,
            stdcycletime,
            linename,
            delaytime,
            cavity,
          } = part;
          this.plan.machinedetails.push({
            machinename,
            stdcycletime,
            linename,
            delaytime,
            activecavity: cavity,
          });
        });
      }
    },
    async fetchEstimatedEnd() {
      if (this.plan.plannedquantity && this.plan.scheduledstart) {
        const machineCount = this.plan.machinedetails.length;
        const totalStdCycletime = this.plan.machinedetails.reduce((acc, cur) => {
          acc += (+cur.stdcycletime * 1000);
          cur.activecavity = +cur.activecavity;
          cur.delaytime = +cur.delaytime;
          return acc;
        }, 0);
        let ctAvg = 0;
        if (machineCount !== 0) {
          ctAvg = Math.floor(totalStdCycletime / machineCount);
        }
        const runTime = ((+this.plan.plannedquantity - 1)
          * ctAvg) + totalStdCycletime;
        this.plan.linestdcycletime = (runTime / 1000);
        this.plan.scheduledend = await this.getScheduledEnd({
          start: new Date(this.plan.scheduledstart).getTime(),
          duration: runTime,
        });
      } else {
        this.plan.scheduledend = '';
      }
    },
    onQuantityChange() {
      this.fetchEstimatedEnd();
    },
    async setSortIndex() {
      const lastPlan = await this.fetchLastPlan();
      if (lastPlan) {
        this.plan.sortindex = lastPlan.sortindex + 100;
      } else {
        this.plan.sortindex = 100;
      }
    },
    exit() {
      this.clear();
      this.selectedPart = null;
      this.$router.push({ name: 'assemblyPlanning' });
    },
    async save() {
      await this.setSortIndex();
      let payload = [];
      if (this.isLinePartMoldCustomerMode) {
        payload = [{
          ...this.plan,
          assetid: this.plan.assetid,
          ...(this.isShowColor ? { color: this.selectedColor } : {}),
          scheduledstart: new Date(this.plan.scheduledstart).getTime(),
          familyName: 'family',
        }];
      } else {
        const matrixTags = this.partMatrixTags(this.plan.assetid);
        const partMatrix = matrixTags.reduce((acc, cur) => {
          acc[cur.tagName] = this.selectedMatrix[cur.tagName];
          return acc;
        }, {});
        payload = [{
          ...partMatrix,
          ...this.plan,
          scheduledstart: new Date(this.plan.scheduledstart).getTime(),
          familyName: 'family',
        }];
      }
      const created = await this.createPlans(payload);
      if (created) {
        this.setAlert({
          show: true,
          type: 'success',
          message: 'PLAN_CREATED',
        });
      } else {
        this.setAlert({
          show: true,
          type: 'error',
          message: 'ERROR_CREATING_PLAN',
        });
      }
      return created;
    },
    async onSaveAndAddNew() {
      const isValid = await this.$refs.form.validate();
      this.validationErrors = isValid ? [] : this.collectValidationErrors();
      if (!isValid) {
        return;
      }
      this.savingAndNew = true;
      const created = await this.save();
      if (created) {
        this.clear();
        this.selectedPart = null;
      }
      this.validationErrors = [];
      this.savingAndNew = false;
    },
    async onSaveAndExit() {
      const isValid = await this.$refs.form.validate();
      this.validationErrors = isValid ? [] : this.collectValidationErrors();
      if (!isValid) {
        return;
      }
      this.savingAndExit = true;
      const created = await this.save();
      if (created) {
        this.exit();
      }
      this.validationErrors = [];
      this.savingAndExit = false;
    },
  },
};
</script>
